import React, { useState, useEffect } from "react";
import { FaBuilding, FaUser } from "react-icons/fa";
import axios from "axios";

const API_URL = require("../../config/apiConfig").API_URL;

const PaymentModeSelector = ({
  selectedMode,
  onModeChange,
  onOptionsChange,
  userId,
  corporateAccountId,
  corporateMemberId,
  corporateRole,
  fare,
  isLoading = false,
  token,
}) => {
  const [availableOptions, setAvailableOptions] = useState({
    personal: {
      available: true,
      methods: {
        cod: { available: true },
        online: { available: true },
        wallet: { available: true, balance: 0 }
      }
    },
    company: { available: true, reason: null },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    if (!userId || !fare || !token) return;

    const validatePaymentOptions = async () => {
      setLoading(true);
      setError(null);
      setValidationAttempted(true);
      try {
        const headers = {
          "Content-Type": "application/json",
        };

        // Only add auth header if token is provided
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.post(
          `${API_URL}/booking/validate/payment-options`,
          {
            userId,
            corporateAccountId,
            corporateMemberId,
            fare,
          },
          { headers }
        );

        if (response.data?.success) {
          setAvailableOptions(response.data.data);
          // Emit options to parent component
          if (onOptionsChange) {
            onOptionsChange(response.data.data);
          }
        }
      } catch (err) {
        console.error("Error validating payment options:", err);
        // Silently fail - use default options
        // The backend will validate at payment confirmation time
      } finally {
        setLoading(false);
      }
    };

    validatePaymentOptions();
  }, [userId, corporateAccountId, fare, token]);

  // Check if user is a corporate member (staff or admin)
  const isCorporateUser = !!(corporateAccountId || corporateMemberId);
  // Check if corporate user is an admin
  const isCorporateAdmin = corporateRole === 'admin';

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-base text-gray-800">How would you like to pay?</h3>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
        {/* Personal Payment Mode - Hidden for corporate admins */}
        {isCorporateUser && !isCorporateAdmin && (
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            border: `2px solid ${selectedMode === "PERSONAL" ? "#3B82F6" : "#E5E7EB"}`,
            borderRadius: "0.75rem",
            padding: "1rem",
            cursor: isLoading || loading ? "not-allowed" : "pointer",
            backgroundColor: selectedMode === "PERSONAL" ? "#EFF6FF" : "white",
            opacity: isLoading || loading ? 0.5 : 1,
            pointerEvents: isLoading || loading ? "none" : "auto",
            transition: "all 0.2s ease",
          }}
          className={`${selectedMode !== "PERSONAL" && !isLoading && !loading ? "hover:shadow-md" : ""}`}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            <input
              type="radio"
              name="paymentMode"
              value="PERSONAL"
              checked={selectedMode === "PERSONAL"}
              onChange={(e) => onModeChange(e.target.value)}
              disabled={isLoading || loading}
              style={{ marginTop: "0.25rem", cursor: "pointer", accentColor: "#2563EB" }}
            />

            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <FaUser style={{ color: "#2563EB", fontSize: "1.125rem" }} />
                <p style={{ fontWeight: "600", color: "#111827" }}>Pay Personally</p>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#4B5563" }}>
                Use cash, online payment, or your personal wallet
              </p>
            </div>
          </div>

          {availableOptions.personal.methods && (
            <ul style={{ fontSize: "0.75rem", color: "#4B5563", marginLeft: "1.75rem", listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {availableOptions.personal.methods.cod?.available && (
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ color: "#16A34A" }}>✓</span> Cash on Delivery
                </li>
              )}
              {availableOptions.personal.methods.online?.available && (
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ color: "#2563EB" }}>✓</span> Online Payment
                </li>
              )}
              {availableOptions.personal.methods.wallet?.available && (
                <li style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ color: "#9333EA" }}>✓</span> Wallet (₹{availableOptions.personal.methods.wallet.balance?.toLocaleString("en-IN")})
                </li>
              )}
            </ul>
          )}
        </label>
        )}

        {/* Company Payment Mode - Only for corporate users */}
        {isCorporateUser && (
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            border: `2px solid ${selectedMode === "COMPANY" ? "#6366F1" : !availableOptions.company.available ? "#FCA5A5" : "#E5E7EB"}`,
            borderRadius: "0.75rem",
            padding: "1rem",
            cursor: isLoading || loading ? "not-allowed" : "pointer",
            backgroundColor: selectedMode === "COMPANY" ? "#E0E7FF" : !availableOptions.company.available ? "#FEF2F2" : "white",
            opacity: isLoading || loading ? 0.6 : 1,
            pointerEvents: isLoading || loading ? "none" : "auto",
            transition: "all 0.2s ease",
          }}
          className={`${selectedMode !== "COMPANY" && !isLoading && !loading && availableOptions.company.available ? "hover:shadow-md" : ""}`}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            <input
              type="radio"
              name="paymentMode"
              value="COMPANY"
              checked={selectedMode === "COMPANY"}
              onChange={(e) => onModeChange(e.target.value)}
              disabled={isLoading || loading}
              style={{ marginTop: "0.25rem", cursor: isLoading || loading ? "not-allowed" : "pointer", accentColor: "#4F46E5" }}
            />

            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
                <FaBuilding style={{ color: "#4F46E5", fontSize: "1.125rem" }} />
                <p style={{ fontWeight: "600", color: "#111827" }}>Pay via Company</p>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#4B5563" }}>
                Bill this ride to your company account
              </p>
            </div>
          </div>

          {!availableOptions.company.available && availableOptions.company.reason ? (
            <div style={{ fontSize: "0.75rem", color: "#DC2626", marginLeft: "1.75rem", fontWeight: "500", backgroundColor: "#FEE2E2", padding: "0.5rem", borderRadius: "0.375rem", display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
              <span style={{ marginTop: "0.125rem" }}>⚠️</span>
              <span>{availableOptions.company.reason}</span>
            </div>
          ) : availableOptions.company.billingModel ? (
            <div style={{ fontSize: "0.75rem", color: "#4338CA", marginLeft: "1.75rem", fontWeight: "500", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
              {availableOptions.company.billingModel === "wallet" ? (
                <p style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span>💳</span> Company Wallet: ₹{availableOptions.company.availableBalance?.toLocaleString("en-IN")}
                </p>
              ) : (
                <p style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span>💳</span> Available Balance: ₹{availableOptions.company.availableBalance?.toLocaleString("en-IN")}
                  {availableOptions.company.walletBalance > 0 && (
                    <span style={{ fontSize: "0.7rem", color: "#6B7280" }}>
                      (Includes ₹{availableOptions.company.walletBalance?.toLocaleString("en-IN")} wallet)
                    </span>
                  )}
                </p>
              )}
            </div>
          ) : (
            <p style={{ fontSize: "0.75rem", color: "#6B7280", marginLeft: "1.75rem" }}>
              Company account details will be validated at checkout
            </p>
          )}
        </label>
        )}
      </div>
    </div>
  );
};

export default PaymentModeSelector;
